import type React from "react";
import { useEffect, useRef, useState } from "react";
import style from "./SvgAnnotation.module.scss";
import { AnnotationPopover } from "./components/AnnotationPopover";
import { RenderAnnotation } from "./components/renderAnnotation";

interface Annotation {
	[key: string]: { word: string; x: number; y: number };
}

export const SvgAnnotation: React.FC<{ svgPath: string }> = ({ svgPath }) => {
	const [selectedPart, setSelectedPart] = useState<string | null>(null);
	const [annotationsMap, setAnnotationsMap] = useState<Annotation>({});
	const [svgIds, setSvgIds] = useState<string[]>([]);
	const svgRef = useRef<SVGSVGElement | null>(null);
	const [popupOpen, setPopupOpen] = useState(false);
	const [clickedItem, setClickedItem] = useState<{
		x: number | null;
		y: number | null;
	}>({
		x: null,
		y: null,
	});
	const svgElement = svgRef.current;
	const viewBox = svgElement?.viewBox.baseVal;
	const [rerender, setRerender] = useState(false);
	const w = 2122;
	const h = 2122;

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		initializeSvgInteractions();
		return () => {
			if (svgElement) {
				for (const partId of svgIds) {
					const element = svgElement.getElementById(partId);
					if (element) {
						element.removeEventListener("click", (event: Event) =>
							handlePartClick(event, partId),
						);
					}
				}
			}
		};
	}, [svgPath, viewBox]);

	const initializeSvgInteractions = () => {
		if (svgElement && svgPath) {
			svgElement.innerHTML = svgPath;
			svgElement.style.cursor = "pointer";

			// Query both g[data-word-id] and path[data-word-id] elements
			const svgElementsWithDataWordId = svgElement.querySelectorAll(
				"g[data-word-id], path[data-word-id]",
			);

			// Extract data-word-id attributes and filter out null values
			const ids = Array.from(svgElementsWithDataWordId)
				.map((element) => element.getAttribute("data-word-id"))
				.filter((id) => id !== null) as string[];

			// Set SVG ids in state or for later use
			if (ids.length) {
				setSvgIds(ids);
			}

			// Add event listeners to each element with data-word-id
			for (const element of Array.from(svgElementsWithDataWordId)) {
				element.addEventListener("mouseenter", () =>
					handleMouseEnter(element as SVGElement),
				);
				element.addEventListener("mouseleave", () =>
					handleMouseLeave(element as SVGElement),
				);
				(element as SVGElement).addEventListener("click", (event: MouseEvent) =>
					handlePartClick(
						event,
						(element as SVGElement)?.getAttribute("data-word-id") as string,
					),
				);
			}
		}
	};

	const handleMouseEnter = (element: SVGElement) => {
		element.dataset.originalFill =
			element.style.fill || element.getAttribute("fill") || "none";
		element.style.fill = "grey";
	};

	const handleMouseLeave = (element: SVGElement) => {
		if (element?.dataset?.originalFill) {
			element.style.fill = element.dataset.originalFill;
		}
	};

	const handlePartClick = (event: Event, partId: string) => {
		const mouseEvent = event as MouseEvent;
		mouseEvent.stopPropagation();
		setRerender(!rerender);
		setPopupOpen(true);
		const svgElement = svgRef.current;
		if (svgElement) {
			const rect = svgElement.getBoundingClientRect();
			const x = ((mouseEvent?.clientX - rect?.left) / rect?.width) * w;
			const y = ((mouseEvent?.clientY - rect?.top) / rect?.height) * h;
			setClickedItem({ x, y });
			setSelectedPart(partId);
		}
	};

	const checkAnnotations = () => {
		for (const partId in annotationsMap) {
			const correct: boolean = annotationsMap[partId].word === partId;
			setAnnotationsMap((prevAnnotations) => ({
				...prevAnnotations,
				[partId]: { ...prevAnnotations[partId], correct },
			}));
			RenderAnnotation(
				svgElement,
				partId,
				annotationsMap[partId].word,
				annotationsMap[partId].x,
				annotationsMap[partId].y,
				correct,
				handleRemoveAnnotation,
			);
		}
	};

	const handleWordSelect = (word: string) => {
		setPopupOpen(false);
		if (selectedPart && clickedItem.x && clickedItem.y) {
			setAnnotationsMap((prevAnnotations) => ({
				...prevAnnotations,
				[selectedPart]: {
					word,
					x: clickedItem?.x || 0,
					y: clickedItem?.y || 0,
					correct: null,
				},
			}));
			RenderAnnotation(
				svgElement,
				selectedPart,
				word,
				clickedItem.x,
				clickedItem.y,
				null,
				handleRemoveAnnotation,
			);
			setSelectedPart(null);
		}
	};

	const handleRemoveAnnotation = (partId: string) => {
		setAnnotationsMap((prevAnnotations) => {
			const updatedAnnotations = { ...prevAnnotations };
			delete updatedAnnotations[partId];
			return updatedAnnotations;
		});
		const svgElement = svgRef.current;
		if (svgElement) {
			const annotationElement = svgElement.getElementById(
				`annotation-${partId}`,
			);
			if (annotationElement) {
				svgElement.removeChild(annotationElement);
			}
		}
	};

	const reset = () => {
		setPopupOpen(false);
		setSelectedPart(null);
		setClickedItem({ x: null, y: null });
		setAnnotationsMap({});
		initializeSvgInteractions();
	};

	const closePopup = () => {
		setPopupOpen(false);
	};

	return (
		<div
			onClick={closePopup}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					closePopup();
				}
			}}
			className={style.svgContainer}
		>
			<div className={style.buttonContainer}>
				<button
					type="button"
					className={style.checkButton}
					onClick={checkAnnotations}
				>
					Check
				</button>
				<button type="button" className={style.resetButton} onClick={reset}>
					Reset
				</button>
			</div>
			<svg
				ref={svgRef}
				xmlns="http://www.w3.org/2000/svg"
				viewBox={`0 0 ${w} ${h}`}
			/>
			<AnnotationPopover
				selectedPart={selectedPart}
				clickedItem={clickedItem}
				svgViewBox={{ width: w, height: h }}
				popupOpen={popupOpen}
				svgIds={svgIds}
				annotationsMap={annotationsMap}
				handleWordSelect={handleWordSelect}
			/>
		</div>
	);
};
